import type { Fiche } from '@pharmafiche/schema';
import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

interface PharmaFicheDB extends DBSchema {
  fiches: {
    key: string;
    value: Fiche;
    indexes: { 'by-updated': string };
  };
  versions: {
    key: [string, string]; // [ficheId, versionName]
    value: {
      fiche_id: string;
      name: string;
      created_at: string;
      fiche: Fiche;
    };
    indexes: { 'by-fiche': string };
  };
}

const DB_NAME = 'pharmafiche';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<PharmaFicheDB>> | null = null;

function getDb(): Promise<IDBPDatabase<PharmaFicheDB>> {
  if (!dbPromise) {
    dbPromise = openDB<PharmaFicheDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('fiches')) {
          const store = db.createObjectStore('fiches', { keyPath: 'id' });
          store.createIndex('by-updated', 'meta.updated_at');
        }
        if (!db.objectStoreNames.contains('versions')) {
          const v = db.createObjectStore('versions', { keyPath: ['fiche_id', 'name'] });
          v.createIndex('by-fiche', 'fiche_id');
        }
      },
    });
  }
  return dbPromise;
}

export async function saveFiche(fiche: Fiche): Promise<void> {
  const db = await getDb();
  await db.put('fiches', fiche);
}

export async function loadFiche(id: string): Promise<Fiche | undefined> {
  const db = await getDb();
  return db.get('fiches', id);
}

export interface FicheSummary {
  id: string;
  title: string;
  template_id: Fiche['meta']['template_id'];
  language: Fiche['meta']['language'];
  updated_at: string;
  created_at: string;
  tag_count: number;
  section_count: number;
}

export async function listFiches(): Promise<FicheSummary[]> {
  const db = await getDb();
  // Most-recently-updated first via the by-updated index, reversed.
  const all = await db.getAllFromIndex('fiches', 'by-updated');
  return all
    .map((f) => ({
      id: f.id,
      title: f.meta.title,
      template_id: f.meta.template_id,
      language: f.meta.language,
      updated_at: f.meta.updated_at,
      created_at: f.meta.created_at,
      tag_count: f.meta.tags.length,
      section_count: f.sections.length,
    }))
    .sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
}

export async function deleteFiche(id: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(['fiches', 'versions'], 'readwrite');
  await tx.objectStore('fiches').delete(id);
  // Cascade: delete all versions of this fiche
  const versionsByFiche = await tx.objectStore('versions').index('by-fiche').getAllKeys(id);
  for (const k of versionsByFiche) {
    await tx.objectStore('versions').delete(k);
  }
  await tx.done;
}

export async function saveVersion(ficheId: string, name: string, fiche: Fiche): Promise<void> {
  const db = await getDb();
  await db.put('versions', {
    fiche_id: ficheId,
    name,
    created_at: new Date().toISOString(),
    fiche,
  });
}

export interface VersionSummary {
  fiche_id: string;
  name: string;
  created_at: string;
}

export async function listVersions(ficheId: string): Promise<VersionSummary[]> {
  const db = await getDb();
  const entries = await db.getAllFromIndex('versions', 'by-fiche', ficheId);
  return entries
    .map((e) => ({ fiche_id: e.fiche_id, name: e.name, created_at: e.created_at }))
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
}

export async function loadVersion(ficheId: string, name: string): Promise<Fiche | undefined> {
  const db = await getDb();
  const entry = await db.get('versions', [ficheId, name]);
  return entry?.fiche;
}

export async function deleteVersion(ficheId: string, name: string): Promise<void> {
  const db = await getDb();
  await db.delete('versions', [ficheId, name]);
}

/** Closes the DB and resets the cached promise. Tests only. */
export async function _resetDb(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
    dbPromise = null;
  }
}
